// Central firm data load for the shell (cases, tasks, meetings, firm users),
// extracted from App.loadData so its tenant / generation guard is testable.
//
// isCurrent() must return false once the session has left ACTIVE or moved to a
// new generation (tenant X → Y). A load started for firm X / generation N then
// never updates state — nor writes the offline cache — after that point.
// Offline entries are keyed by uid + firmId (offlineCacheKey), so Firm X's
// cached records can never be read under Firm Y.
export async function loadFirmData({ uid, firmId, online, isCurrent, services, cache, set }) {
  if (!uid || !firmId) return { loaded: false, reason: 'NO_TENANT' };
  const key = (name) => cache.key(name, uid, firmId);

  if (!online) {
    try {
      const cachedCases = await cache.get(key('cases'));
      const cachedTasks = await cache.get(key('tasks'));
      const cachedMeetings = await cache.get(key('meetings'));
      if (!isCurrent()) return { loaded: false, reason: 'STALE' };
      if (cachedCases?.cases) set.cases(cachedCases.cases);
      if (cachedTasks?.tasks) set.tasks(cachedTasks.tasks);
      if (cachedMeetings?.meetings) set.meetings(cachedMeetings.meetings);
    } catch (e) { console.log('Offline cache read error:', e); }
    if (!isCurrent()) return { loaded: false, reason: 'STALE' };
    set.loading(false);
    return { loaded: true, offline: true };
  }

  const casesResult = await services.getCases(uid, firmId);
  if (!isCurrent()) return { loaded: false, reason: 'STALE' };
  if (casesResult.success) {
    set.cases(casesResult.cases);
    try { cache.put(key('cases'), { cases: casesResult.cases }, 120); } catch (e) { /* ignore */ }
  }

  const tasksResult = await services.getEnhancedTasks(uid, {}, firmId);
  if (!isCurrent()) return { loaded: false, reason: 'STALE' };
  if (tasksResult.success) {
    set.tasks(tasksResult.tasks);
    try { cache.put(key('tasks'), { tasks: tasksResult.tasks }, 120); } catch (e) { /* ignore */ }
  }

  const meetingsResult = await services.getMeetings(uid, firmId);
  if (!isCurrent()) return { loaded: false, reason: 'STALE' };
  if (meetingsResult.success) {
    set.meetings(meetingsResult.meetings);
    try { cache.put(key('meetings'), { meetings: meetingsResult.meetings }, 120); } catch (e) { /* ignore */ }
  }

  const usersResult = await services.getFirmUsers(firmId);
  if (!isCurrent()) return { loaded: false, reason: 'STALE' };
  if (usersResult.success) set.firmUsers(usersResult.users);
  set.loading(false);
  return { loaded: true };
}
